import { computed, unref, type MaybeRef } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { useEventView } from '@/composables/useEventView'
import { useTryoutSessionInvalidation } from '@/composables/useTryoutContext'
import { athleteFeedbackAPI } from '@/api/athleteFeedback'

export function useAthleteFeedback(enabled: MaybeRef<boolean> = true) {
  const view = useEventView()
  const scope = computed(() => ({
    clubId: view.scope.value.clubId,
    seasonId: view.scope.value.seasonId,
    tryoutId: view.scope.value.eventId,
  }))
  const key = computed(() => [
    'athleteFeedback',
    scope.value.clubId,
    scope.value.seasonId,
    scope.value.tryoutId,
    view.scope.value.viewerId,
    view.mode.value,
  ])
  const api = computed(() => athleteFeedbackAPI(scope.value))
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => api.value.load(view.mode.value!, signal),
    enabled: computed(
      () => unref(enabled) && view.active.value && !!view.mode.value
    ),
    retry: false,
    gcTime: 0,
  })
  useTryoutSessionInvalidation(
    computed(() => scope.value.clubId),
    computed(() => scope.value.seasonId),
    computed(() => scope.value.tryoutId)
  )
  const client = useQueryClient()
  const refresh = () =>
    client.invalidateQueries({
      queryKey: [
        'athleteFeedback',
        scope.value.clubId,
        scope.value.seasonId,
        scope.value.tryoutId,
      ],
    })
  return { view, query, api, refresh, key }
}
